import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  addOrganisationMember,
  organisationMembers,
  removeOrganisationMember,
} from '../api/endpoints'
import type { Organisation, OrganisationMember, OrganisationRole, User } from '../api/types'
import { Modal } from './Modal'

const ROLES: { value: OrganisationRole; label: string; what: string }[] = [
  { value: 'OWNER', label: 'Owner', what: 'Everything, including who is in the organisation.' },
  { value: 'MANAGER', label: 'Manager', what: 'Creates and runs events.' },
  { value: 'STAFF', label: 'Staff', what: 'Watches events and answers reports.' },
]

/**
 * Who is in the organisation, and what each of them may do.
 *
 * Marshals are the reason this exists. Somebody refereeing needs to see every faction at once, and
 * the alternative to giving them STAFF is handing them an owner's account — which is how one
 * person ends up able to delete the club's events because they were helping on a Saturday.
 */
export function MembersPanel({
  organisation,
  me,
  onClose,
}: {
  organisation: Organisation
  me: User
  onClose: () => void
}) {
  const [members, setMembers] = useState<OrganisationMember[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<OrganisationRole>('STAFF')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const result = await organisationMembers(organisation.id)
    if (result.ok) setMembers(result.value)
    else setError(result.error.message)
  }, [organisation.id])

  useEffect(() => {
    void load()
  }, [load])

  // Only an owner changes who is in the organisation. A manager runs events; that is a different
  // job, and one of them should not imply the other.
  const canAdminister = organisation.myRole === 'OWNER'
  const owners = members?.filter((member) => member.role === 'OWNER').length ?? 0

  async function add(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    const result = await addOrganisationMember(organisation.id, { email: email.trim() }, role)
    setBusy(false)

    if (!result.ok) {
      setError(result.error.message)
      return
    }
    setEmail('')
    await load()
  }

  async function change(member: OrganisationMember, next: OrganisationRole) {
    setError(null)
    // The same call that adds somebody changes what they may do; the server upserts the membership.
    const result = await addOrganisationMember(organisation.id, { userId: member.userId }, next)
    if (!result.ok) setError(result.error.message)
    await load()
  }

  async function remove(member: OrganisationMember) {
    if (!confirm(`Remove ${member.callsign} from ${organisation.name}?`)) return
    setError(null)
    const result = await removeOrganisationMember(organisation.id, member.userId)
    if (!result.ok) setError(result.error.message)
    await load()
  }

  return (
    <Modal title={`Members of ${organisation.name}`} onClose={onClose}>
      <div className="stack">
        {error && <div className="banner danger">{error}</div>}

        {members === null ? (
          <p className="tertiary">Loading…</p>
        ) : (
          <div className="stack">
            {members.map((member) => {
              const isMe = member.userId === me.id
              // An organisation with no owner is one nobody can administer, and the only way back
              // is a database edit. The server refuses it; saying so here is kinder than a banner
              // after the fact.
              const lastOwner = member.role === 'OWNER' && owners <= 1

              return (
                <div key={member.userId} className="card spread" style={{ padding: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {member.callsign}
                      {isMe && <span className="tertiary"> · you</span>}
                    </div>
                    <div className="tertiary">
                      {ROLES.find((entry) => entry.value === member.role)?.what}
                    </div>
                  </div>

                  <div className="row">
                    {canAdminister ? (
                      <>
                        <select
                          aria-label={`Role for ${member.callsign}`}
                          value={member.role}
                          disabled={lastOwner}
                          onChange={(event) =>
                            void change(member, event.target.value as OrganisationRole)
                          }
                        >
                          {ROLES.map((entry) => (
                            <option key={entry.value} value={entry.value}>
                              {entry.label}
                            </option>
                          ))}
                        </select>
                        <button
                          className="ghost"
                          disabled={lastOwner}
                          title={lastOwner ? 'The only owner cannot be removed' : undefined}
                          onClick={() => void remove(member)}
                        >
                          Remove
                        </button>
                      </>
                    ) : (
                      <span className="pill neutral">{member.role}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {canAdminister && (
          <form className="stack" onSubmit={add}>
            <h3 style={{ margin: 0 }}>Add somebody</h3>

            <div className="field">
              <label htmlFor="member-email">Email</label>
              <input
                id="member-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="marshal@myclub.it"
              />
              <p className="tertiary" style={{ marginTop: 6 }}>
                The address they registered with. They need an account on this server first — this
                does not send an invitation.
              </p>
            </div>

            <div className="field">
              <label htmlFor="member-role">Role</label>
              <select
                id="member-role"
                value={role}
                onChange={(event) => setRole(event.target.value as OrganisationRole)}
              >
                {ROLES.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label} — {entry.what}
                  </option>
                ))}
              </select>
            </div>

            <button type="submit" disabled={busy || !email.trim()}>
              {busy ? 'Adding…' : 'Add'}
            </button>
          </form>
        )}
      </div>
    </Modal>
  )
}
